import React from "react";

import { Button as AntButton, ButtonProps as AntButtonProps } from "antd";

import { ProductEventProps, goTo, productAnalytics, trackMixpanel, useNavigate } from "utils";

import { Spin } from "components";

export type ButtonType = "primary" | "secondary" | "text" | "light primary" | "light secondary" | "light text" | "tertiary" | "link" | "danger";

export type ButtonProps = Omit<AntButtonProps, "type"> &
  ProductEventProps & {
    dataTestId?: string;
    loading?: boolean;
    type?: ButtonType;
    mixpanelText?: string;
    iconLeft?: boolean;
  };

enum spinSize {
  small = "small",
  middle = "default",
  large = "large",
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      type,
      size,
      style,
      disabled,
      loading,
      onClick,
      onKeyDown,
      htmlType,
      className,
      block,
      id,
      icon,
      iconLeft,
      dataTestId,
      mixpanelText,
      productEvent,
      productEventProperties,
      href, // DO NOT add href as a prop of buttons
      target,
      title,
      role,
      "aria-label": ariaLabel,
      "aria-haspopup": ariaHasPopup,
      "aria-expanded": ariaExpanded,
      "aria-checked": ariaChecked,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const forceIconLeft = iconLeft || type === "text" || type === "light text";

    const baseType = type === "tertiary" ? "icon-tertiary" : type;
    const allClassNames = [baseType, className ?? ""].join(" ").trim();

    return (
      <AntButton
        ref={ref}
        data-testid={dataTestId}
        // @ts-expect-error We have added custom css classes matching the type
        type={type}
        size={size}
        style={style}
        disabled={disabled || loading}
        onClick={(e) => {
          trackMixpanel("ClickButton", { buttonText: mixpanelText || (typeof children === "string" && children) || "undefined" });
          if (productEvent) productAnalytics.track(productEvent, productEventProperties);
          if (href) {
            goTo(e, navigate, href);
          }
          onClick?.(e);
        }}
        onKeyDown={onKeyDown}
        htmlType={htmlType}
        className={allClassNames}
        title={title}
        role={role}
        aria-label={ariaLabel}
        aria-haspopup={ariaHasPopup}
        aria-expanded={ariaExpanded}
        aria-checked={ariaChecked}
        block={block}
        id={id}
        target={target || "_self"}
      >
        {forceIconLeft ? icon : null}
        {loading && <Spin size={spinSize[size || "middle"]} />}
        {children}
        {forceIconLeft ? null : icon}
      </AntButton>
    );
  }
);

Button.displayName = "Button";

export default Button;
